import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { AlarmsManagerService } from '../../services/alarms-manager.service';
import * as AlarmsManagerActions from '../states/alarms-manager/alarms-manager.action';

@Injectable()
export class AlarmsManagerEffects {
  constructor(
    private actions$: Actions,
    private alarmsManagerService: AlarmsManagerService,
  ) {}

  getAlarms$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AlarmsManagerActions.getAlarms),
      switchMap((action) =>
        this.alarmsManagerService.getAlarms(action.query).pipe(
          map((response) => AlarmsManagerActions.getAlarmsSuccess({ response })),
          catchError((error) => 
            of(AlarmsManagerActions.getAlarmsFail({ error: error.message || 'Failed to load alarms' }))
          )
        )
      )
    )
  );

  getAlarmById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AlarmsManagerActions.getAlarmById),
      switchMap((action) =>
        this.alarmsManagerService.getAlarmById(action.alarmId).pipe(
          map((alarm) => AlarmsManagerActions.getAlarmByIdSuccess({ alarm })),
          catchError((error) => 
            of(AlarmsManagerActions.getAlarmByIdFail({ error: error.message || 'Failed to load alarm' }))
          )
        )
      )
    )
  );

  getAlarmWithAlerts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AlarmsManagerActions.getAlarmWithAlerts),
      tap((action) => console.log('Effect triggered: getAlarmWithAlerts', action)),
      switchMap((action) =>
        this.alarmsManagerService.getAlarmWithAlerts(action.alarmId).pipe(
          tap((response) => console.log('API response:', response)),
          map((alarmWithAlerts) => AlarmsManagerActions.getAlarmWithAlertsSuccess({ alarmWithAlerts })),
          catchError((error) => {
            console.error('Effect error:', error);
            return of(AlarmsManagerActions.getAlarmWithAlertsFail({ error: error.message || 'Failed to load alarm with alerts' }));
          })
        )
      )
    )
  );

  closeAlarm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AlarmsManagerActions.closeAlarm),
      switchMap((action) =>
        this.alarmsManagerService.closeAlarm(action.alarmId, action.userId).pipe(
          map(() => AlarmsManagerActions.closeAlarmSuccess({ alarmId: action.alarmId })),
          catchError((error) => 
            of(AlarmsManagerActions.closeAlarmFail({ error: error.message || 'Failed to close alarm' }))
          )
        )
      )
    )
  );

  closeAlarmSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AlarmsManagerActions.closeAlarmSuccess),
      map(() => AlarmsManagerActions.getAlarms({ query: {} }))
    )
  );

  getAlarmStats$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AlarmsManagerActions.getAlarmStats),
      switchMap(() =>
        this.alarmsManagerService.getAlarmStats().pipe(
          map((stats) => AlarmsManagerActions.getAlarmStatsSuccess({ stats })),
          catchError((error) => 
            of(AlarmsManagerActions.getAlarmStatsFail({ error: error.message || 'Failed to load alarm stats' }))
          )
        )
      )
    )
  );

  checkTimeouts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AlarmsManagerActions.checkTimeouts),
      switchMap(() =>
        this.alarmsManagerService.checkTimeouts().pipe(
          map((result) => AlarmsManagerActions.checkTimeoutsSuccess({ updatedCount: result.updatedCount })),
          catchError((error) => 
            of(AlarmsManagerActions.checkTimeoutsFail({ error: error.message || 'Failed to check timeouts' }))
          )
        )
      )
    )
  );

  checkTimeoutsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AlarmsManagerActions.checkTimeoutsSuccess),
      map(() => AlarmsManagerActions.getAlarms({ query: {} }))
    )
  );

  // Auto-refresh stats when alarms are successfully loaded
  getAlarmsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AlarmsManagerActions.getAlarmsSuccess),
      map(() => AlarmsManagerActions.getAlarmStats())
    )
  );
}